import ExcelJS from "exceljs";

export const computeOrderSavings = (
  activeProduct,
  distributorOptions,
  activeKey,
) => {
  if (!activeProduct || !Array.isArray(distributorOptions)) return null;
  const altBest = distributorOptions
    .filter((o) => o.key !== activeKey)
    .reduce(
      (min, o) =>
        !min || o.product.totalPrice < min.product.totalPrice ? o : min,
      null,
    );
  if (!altBest || altBest.product.totalPrice <= (activeProduct.totalPrice ?? 0))
    return null;
  return parseFloat(
    (altBest.product.totalPrice - (activeProduct.totalPrice ?? 0)).toFixed(2),
  );
};

export const abbreviatePackageType = (packageType) => {
  if (!packageType) return "";
  const match = packageType.match(/\(([^)]+)\)/);
  if (match) return match[1];
  return packageType.replace(/[®™]/g, "").trim();
};

// Some distributors (e.g. Digikey) list the same price breaks under two
// different packaging labels (Cut Tape vs Digi-Reel) — same stock, same
// price at every qty, just a different reel format. Signature ignores the
// label so those collapse into one packaging option instead of showing twice.
const priceListSignature = (priceList) =>
  JSON.stringify(
    (priceList || []).map((slab) => [
      slab.min_qty,
      slab.price ?? slab.actual_price,
    ]),
  );

// Dedupes an already-sorted packageMap so identical price breaks under
// different packaging labels (Cut Tape / Digi-Reel) collapse into a single
// entry, keeping the first (cheaper/preferred) label's fields but recording
// every label that shared this price list in `mergedLabels`, so the UI can
// show "Cut Tape (CT) & Digi-Reel®" instead of losing the second label.
const dedupePackageOptionsByPriceList = (packageOptions) => {
  const bySignature = new Map();
  for (const p of packageOptions) {
    const sig = priceListSignature(p.price?.price_list);
    const existing = bySignature.get(sig);
    if (existing) {
      existing.mergedLabels.push(p.price?.package_type ?? "");
    } else {
      bySignature.set(sig, { ...p, mergedLabels: [p.price?.package_type ?? ""] });
    }
  }
  return [...bySignature.values()];
};

// Digikey's own site always lists Cut Tape/Digi-Reel ahead of Tape & Reel —
// mirror that fixed ordering here instead of letting required-qty pricing
// flip the order, falling back to price for anything that isn't TR.
const isTapeAndReelPackage = (packageType) =>
  /tape\s*&\s*reel|\(tr\)/i.test(packageType || "");

const sortPackageOptions = (packageOptions) =>
  [...packageOptions].sort((a, b) => {
    const aIsTR = isTapeAndReelPackage(a.price?.package_type);
    const bIsTR = isTapeAndReelPackage(b.price?.package_type);
    if (aIsTR !== bIsTR) return aIsTR ? 1 : -1;
    return (a.totalPrice ?? 0) - (b.totalPrice ?? 0);
  });

// Collapse packaging variants of the same distributor + part number (e.g.
// Digikey's CT/TR/Digi-Reel) into a single row, mirroring the main
// Distributor selector. Shared by the "other matches" panel (which renders
// these rows) and the eye-icon count badge, so the two never disagree.
export const groupOtherMatches = (otherMatches) => {
  if (!Array.isArray(otherMatches)) return [];
  const groups = [];
  const indexByKey = {};
  for (const match of otherMatches) {
    const key = `${match.distributor}||${match.part_number}`;
    if (!(key in indexByKey)) {
      indexByKey[key] = groups.length;
      groups.push({ key, variants: [] });
    }
    groups[indexByKey[key]].variants.push(match);
  }
  return groups.map(({ key, variants }) => {
    const representative = variants.reduce(
      (min, p) =>
        !min || (p.totalPrice ?? 0) < (min.totalPrice ?? 0) ? p : min,
      null,
    );
    const packageMap = {};
    for (const p of variants) {
      const packageType = p.price?.package_type ?? "";
      const existing = packageMap[packageType];
      if (!existing || (p.totalPrice ?? 0) < (existing.totalPrice ?? 0)) {
        packageMap[packageType] = p;
      }
    }
    const packageOptions = dedupePackageOptionsByPriceList(
      sortPackageOptions(Object.values(packageMap)),
    );
    return { key, match: representative, packageOptions };
  });
};

export const BOM_ITEM_STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  NO_MPN_FOUND: "no_mpn_found",
  STOCK_NOT_FOUND: "stock_not_found",
  MULTIPLE_MPN_FOUND: "multiple_mpn_found",
  FOUND: "found",
};

export const normalizePartNumber = (pn) =>
  (pn ?? "")
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
    .trim()
    .toLowerCase();

const hasStockInList = (products) =>
  products.some((product) => {
    const av = product.availability;
    return (
      av !== "" &&
      av !== 0 &&
      av !== "0" &&
      av !== "None" &&
      av !== "N/A" &&
      av !== null
    );
  });

export const getBomItemStatus = (item) => {
  if (item.status !== "completed") return item.status;

  const allData = (item.processing_data || []).flatMap((pd) =>
    Array.isArray(pd.data) ? pd.data : [],
  );

  if (allData.length === 0) return BOM_ITEM_STATUS.NO_MPN_FOUND;

  const partLower = normalizePartNumber(item.part_number);
  const exactMatches = allData.filter(
    (product) => normalizePartNumber(product.part_number) === partLower,
  );

  if (exactMatches.length === 0) {
    // No exact match — check global stock to distinguish the two non-exact cases
    return hasStockInList(allData)
      ? BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND
      : BOM_ITEM_STATUS.STOCK_NOT_FOUND;
  }

  // Exact match(es) found — stock must come from those, not from unrelated variants
  return hasStockInList(exactMatches)
    ? BOM_ITEM_STATUS.FOUND
    : BOM_ITEM_STATUS.STOCK_NOT_FOUND;
};

export const bomToolStats = (res) => {
  const bomItems = res.data?.bom_items || [];
  const totalBomCount = bomItems.length;
  const processingBomCount = bomItems.filter(
    (item) => item.status === "processing",
  ).length;
  const pendingBomCount = bomItems.filter(
    (item) => item.status === "pending",
  ).length;
  const completedBomCount = bomItems.filter(
    (item) => item.status === "completed",
  ).length;

  const mpnNotFoundCount = bomItems.filter(
    (item) => getBomItemStatus(item) === BOM_ITEM_STATUS.NO_MPN_FOUND,
  ).length;
  const stockNotFoundCount = bomItems.filter(
    (item) => getBomItemStatus(item) === BOM_ITEM_STATUS.STOCK_NOT_FOUND,
  ).length;
  const multipleMpnFoundCount = bomItems.filter(
    (item) => getBomItemStatus(item) === BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND,
  ).length;

  return {
    totalBomCount,
    processingBomCount,
    pendingBomCount,
    completedBomCount,
    mpnNotFoundCount,
    stockNotFoundCount,
    multipleMpnFoundCount,
  };
};

export const getPartialQtyCount = (processedBomItems) => {
  if (!Array.isArray(processedBomItems)) return 0;

  return processedBomItems.filter((item) => {
    const opt = item?.optimal;
    if (!opt) return false;

    const avail =
      parseInt(String(opt.availability ?? 0).replace(/\D/g, ""), 10) || 0;
    const reqQty =
      parseInt(String(item?.actual_qty ?? item?.qty ?? 0), 10) || 0;

    return avail > 0 && reqQty > 0 && avail < reqQty;
  }).length;
};

const getNumericValue = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "None" ||
    value === "N/A"
  )
    return 0;
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getActualOrderQty = (product, requiredQty) => {
  const minOrder = parseInt(product?.price?.min_order, 10) || 1;
  const multiple = parseInt(product?.price?.multiple, 10) || 1;
  const base = Math.max(requiredQty, minOrder);
  return Math.ceil(base / multiple) * multiple;
};

export const getSlabUnitPrice = (slab) => {
  const raw = parseFloat(slab?.raw_price);
  if (!isNaN(raw)) return raw;
  return parseFloat(slab?.price ?? slab?.actual_price) || 0;
};

// Display string for a slab's unit price. Uses the same value the optimizer
// ranks on (getSlabUnitPrice, which prefers the precise raw_price over the
// API's 2-decimal rounded price) so the shown price never contradicts why a
// distributor was picked. Trailing zeros are trimmed; returns null when the
// slab carries no price at all.
export const formatUnitPrice = (slab) => {
  if (
    slab == null ||
    (slab.price == null && slab.actual_price == null && slab.raw_price == null)
  )
    return null;
  return String(parseFloat(getSlabUnitPrice(slab).toFixed(6)));
};

export const getMatchingPriceSlab = (product, qty) => {
  const priceList = product?.price?.price_list || [];
  let matchingSlab = null;
  for (const slab of priceList) {
    if (slab.min_qty <= qty) {
      matchingSlab = slab;
    }
  }
  return matchingSlab;
};

// Build a cheapest-price-first allocation of `requiredQty` across distributors
// for when no single distributor holds enough stock. `perDistributorProducts`
// is one representative product per distributor (each carrying its own
// availability + price_list). Distributors are ranked by unit price at the
// required qty, then filled greedily until the qty is covered or stock runs
// out. Each segment is priced at the slab its own allocated qty qualifies for
// (quantity-wise pricing). Returns null when no distributor has stock.
export const getCrossDistributorSplit = (
  perDistributorProducts,
  requiredQty,
) => {
  if (!Array.isArray(perDistributorProducts) || requiredQty <= 0) return null;

  const withStock = perDistributorProducts
    .map((product) => ({
      product,
      avail: getNumericValue(product?.displayAvailability ?? product?.availability),
    }))
    .filter((x) => x.avail > 0);
  if (withStock.length === 0) return null;

  const unitPriceFor = (product, qty) => {
    const slab = getMatchingPriceSlab(product, qty) ?? product?.selectedPrice;
    return getSlabUnitPrice(slab);
  };

  const ranked = [...withStock].sort(
    (a, b) =>
      unitPriceFor(a.product, requiredQty) - unitPriceFor(b.product, requiredQty),
  );

  const segments = [];
  let remaining = requiredQty;
  for (const { product, avail } of ranked) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, avail);
    if (take <= 0) continue;
    const slab = getMatchingPriceSlab(product, take) ?? product?.selectedPrice;
    const unitPrice = getSlabUnitPrice(slab);
    segments.push({
      distributor: product.distributor,
      qty: take,
      availability: avail,
      unitPrice,
      currency: product?.price?.currency ?? slab?.currency ?? "",
      slab,
      // Full product for this distributor so the UI can add exactly this
      // segment (at its allocated qty and slab price) straight to the cart.
      product,
    });
    remaining -= take;
  }

  const shortfall = Math.max(remaining, 0);
  return {
    segments,
    fulfilled: shortfall === 0,
    requiredQty,
    coveredQty: requiredQty - shortfall,
    shortfall,
    totalPrice: segments.reduce((sum, s) => sum + s.unitPrice * s.qty, 0),
  };
};

export const getOptimalBomItemResults = (bomItems, allowedDistributors = null) => {
  if (!Array.isArray(bomItems)) return [];

  // Optional distributor filter. `null`/undefined means "no filter" (consider
  // every distributor). A Set/array restricts the optimal pick, the per-
  // distributor options, errors and empty results to just those distributors —
  // so an empty selection yields no purchasable options for any item.
  const allowSet =
    allowedDistributors instanceof Set
      ? allowedDistributors
      : Array.isArray(allowedDistributors)
        ? new Set(allowedDistributors)
        : null;
  const isAllowedDistributor = (dist) => allowSet === null || allowSet.has(dist);

  return bomItems.map((item) => {
    // Prefer the user-edited actual quantity (RFQ QTY column) over the
    // originally-imported qty, so editing it re-picks the matching price slab.
    const requiredQty =
      parseInt(item.actual_qty, 10) || parseInt(item.qty, 10) || 0;
    const itemPartNumber = normalizePartNumber(item.part_number);
    // Item status reflects the full result set (whether the part exists at all),
    // independent of which distributors are currently selected for purchasing.
    const itemStatus = getBomItemStatus(item);

    const processingData = (item.processing_data || []).filter((pd) =>
      isAllowedDistributor(pd.distributor),
    );

    const distributorErrors = processingData
      .filter((pd) => pd.status === "failed" && pd.error_message)
      .map((pd) => ({
        distributor: pd.distributor,
        error_message: pd.error_message,
      }));

    const distributorEmptyResults = processingData
      .filter(
        (pd) =>
          pd.status === "completed" &&
          (!Array.isArray(pd.data) || pd.data.length === 0),
      )
      .map((pd) => ({ distributor: pd.distributor }));

    const allProducts = processingData.flatMap((pd) =>
      (Array.isArray(pd.data) ? pd.data : []).map((product) => ({
        ...product,
        qty: requiredQty,
        distributor: pd.distributor,
      })),
    );

    if (allProducts.length === 0) {
      return {
        id: item.id,
        part_number: item.part_number,
        qty: item.qty,
        actual_qty: item.actual_qty ?? null,
        optimal: null,
        otherMatches: [],
        distributorErrors,
        distributorEmptyResults,
        itemStatus,
      };
    }

    const productsWithSlab = allProducts
      .map((product) => {
        const actualQty = getActualOrderQty(product, requiredQty);

        // When the requested/order qty exceeds what's actually in stock, the
        // buyer can only take the available quantity — so price it at the slab
        // the available stock qualifies for, not the (unreachable) requested qty.
        const availability = getNumericValue(product.availability);
        const capToStock = availability > 0 && availability < actualQty;
        const stockSlab = capToStock
          ? getMatchingPriceSlab(product, availability)
          : null;
        // A product whose smallest price break already exceeds the on-hand stock
        // (e.g. a reel-only line: min_order 10,000 but 259 in stock) can't be
        // ordered from that stock at all. Flag it so it never wins as the
        // optimal/representative on a misleadingly low bulk price — but keep a
        // fallback slab so it still shows (priced for its true min order) in the
        // price-slab modal and other-distributor lists.
        const belowStockMin = capToStock && !stockSlab;
        const slab = stockSlab ?? getMatchingPriceSlab(product, actualQty);
        return slab
          ? {
              ...product,
              selectedPrice: slab,
              actualOrderQty: actualQty,
              belowStockMin,
            }
          : null;
      })
      .filter(Boolean);

    if (productsWithSlab.length === 0) {
      const exactFallback = allProducts.filter(
        (p) => normalizePartNumber(p.part_number) === itemPartNumber,
      );
      const fallbackCandidates =
        exactFallback.length > 0 ? exactFallback : allProducts;
      const otherMatches = fallbackCandidates.map((p) => ({
        ...p,
        selectedPrice: null,
        actualOrderQty: requiredQty,
        totalPrice: 0,
      }));
      return {
        id: item.id,
        part_number: item.part_number,
        qty: item.qty,
        actual_qty: item.actual_qty ?? null,
        optimal: null,
        otherMatches,
        distributorErrors,
        distributorEmptyResults,
        itemStatus,
      };
    }

    // Compute split order candidates: floor(T&R) + fill(CT) for exact-qty fulfillment (Digikey only)
    const splitCandidates = [];
    const splitSourceKeys = new Set();
    for (const product of allProducts) {
      if (!(product.distributor ?? "").toLowerCase().includes("digikey"))
        continue;
      const multiple = parseInt(product?.price?.multiple, 10) || 1;
      if (multiple <= 1) continue;
      const minOrder = parseInt(product?.price?.min_order, 10) || 1;
      const ceilQty =
        Math.ceil(Math.max(requiredQty, minOrder) / multiple) * multiple;
      if (ceilQty <= requiredQty) continue;
      const floorQty = ceilQty - multiple;
      if (floorQty <= 0) continue;
      const fillQty = requiredQty - floorQty;
      if (fillQty <= 0) continue;
      const floorSlab = getMatchingPriceSlab(product, floorQty);
      if (!floorSlab) continue;
      const floorPrice = getSlabUnitPrice(floorSlab);
      let bestFill = null;
      let bestSplitTotal = Infinity;
      for (const fc of allProducts) {
        if (fc === product || fc.distributor !== product.distributor) continue;
        if ((parseInt(fc?.price?.multiple, 10) || 1) >= multiple) continue;
        if (getNumericValue(fc.availability) <= 0) continue;
        const fillActualQty = getActualOrderQty(fc, fillQty);
        const fillSlab = getMatchingPriceSlab(fc, fillActualQty);
        if (!fillSlab) continue;
        const fillUnitPrice = getSlabUnitPrice(fillSlab);
        const splitTotal =
          floorPrice * floorQty + fillUnitPrice * fillActualQty;
        if (splitTotal < bestSplitTotal) {
          bestSplitTotal = splitTotal;
          bestFill = {
            product: fc,
            qty: fillActualQty,
            slab: fillSlab,
            unitPrice: fillUnitPrice,
          };
        }
      }
      if (!bestFill) continue;
      splitSourceKeys.add(
        `${product.distributor}||${product.price?.package_type ?? ""}`,
      );
      const avgPrice = bestSplitTotal / requiredQty;
      const trAbbr = abbreviatePackageType(product.price?.package_type ?? "TR");
      const fillAbbr = abbreviatePackageType(
        bestFill.product.price?.package_type ?? "CT",
      );
      const splitPackageType = `${trAbbr}+${fillAbbr}`;
      const trAvail = getNumericValue(product.availability);
      const fillAvail = getNumericValue(bestFill.product.availability);
      const splitAvailability =
        trAvail >= floorQty && fillAvail >= bestFill.qty ? requiredQty : 0;
      splitCandidates.push({
        ...product,
        isSplitOrder: true,
        splitFirst: { qty: floorQty, slab: floorSlab, unitPrice: floorPrice },
        splitSecond: bestFill,
        price: { ...product.price, package_type: splitPackageType },
        selectedPrice: {
          ...floorSlab,
          raw_price: null,
          price: parseFloat(avgPrice.toFixed(6)),
          actual_price: parseFloat(avgPrice.toFixed(6)),
        },
        actualOrderQty: requiredQty,
        availability: splitAvailability,
        // The reel-packaged (TR) count is often a rounded-down subset of the
        // distributor's true on-hand quantity — the cut-tape/Digi-Reel count
        // matches what the distributor's own site shows for the part, so
        // display the larger of the two instead of only the TR figure.
        displayAvailability: Math.max(trAvail, fillAvail),
      });
    }

    // Drop the ceil-overorder version of any product that has a split candidate,
    // so the exact-qty split is preferred over rounding up.
    const productsWithSlabDeduped = productsWithSlab.filter(
      (p) =>
        !splitSourceKeys.has(
          `${p.distributor}||${p.price?.package_type ?? ""}`,
        ),
    );

    const allCandidatesPool = [...productsWithSlabDeduped, ...splitCandidates];

    const exactMatches = allCandidatesPool.filter(
      (p) => normalizePartNumber(p.part_number) === itemPartNumber,
    );

    const candidates =
      exactMatches.length > 0 ? exactMatches : productsWithSlab;

    const sufficientStock = candidates.filter(
      (p) =>
        getNumericValue(p.availability) >= (p.actualOrderQty ?? requiredQty),
    );
    const partialStock = candidates.filter((p) => {
      const av = getNumericValue(p.availability);
      const orderQty = p.actualOrderQty ?? requiredQty;
      return av > 0 && av >= requiredQty && av < orderQty;
    });
    // Prefer options that can actually be ordered from the on-hand stock; only
    // fall back to below-min-order lines (e.g. a reel-only backorder) when no
    // orderable-from-stock option exists.
    const anyStockOrderable = candidates.filter(
      (p) => getNumericValue(p.availability) > 0 && !p.belowStockMin,
    );
    const anyStock =
      anyStockOrderable.length > 0
        ? anyStockOrderable
        : candidates.filter((p) => getNumericValue(p.availability) > 0);

    const pool =
      sufficientStock.length > 0
        ? sufficientStock
        : partialStock.length > 0
          ? partialStock
          : anyStock;

    if (pool.length === 0) {
      const otherMatches = candidates.map((p) => ({
        ...p,
        totalPrice:
          getSlabUnitPrice(p.selectedPrice) * (p.actualOrderQty ?? requiredQty),
      }));

      return {
        id: item.id,
        part_number: item.part_number,
        qty: item.qty,
        actual_qty: item.actual_qty ?? null,
        optimal: null,
        otherMatches,
        distributorOptions: [],
        distributorErrors,
        distributorEmptyResults,
        itemStatus,
      };
    }

    const calcTotal = (p) =>
      getSlabUnitPrice(p.selectedPrice) * (p.actualOrderQty ?? requiredQty);
    const getEffectiveAvail = (p) =>
      getNumericValue(p.displayAvailability ?? p.availability);

    // When no distributor can fulfill the required quantity (neither full nor
    // partial-with-rounding), rank by availability first so a near-zero-stock
    // distributor doesn't win on price alone. Price still breaks ties on stock.
    const isAnyStockFallback =
      sufficientStock.length === 0 && partialStock.length === 0;

    let optimalIndex = 0;
    pool.forEach((current, index) => {
      const best = pool[optimalIndex];
      if (isAnyStockFallback) {
        const bestAvail = getEffectiveAvail(best);
        const currentAvail = getEffectiveAvail(current);
        if (currentAvail > bestAvail) {
          optimalIndex = index;
          return;
        }
        if (currentAvail < bestAvail) return;
      }
      const bestTotal = calcTotal(best);
      const currentTotal = calcTotal(current);
      if (currentTotal < bestTotal) optimalIndex = index;
    });

    const optimal = pool[optimalIndex];
    const outsidePool = candidates.filter((p) => !pool.includes(p));
    const otherMatches = [
      ...pool.filter((_, index) => index !== optimalIndex),
      ...outsidePool,
    ].map((p) => ({ ...p, totalPrice: calcTotal(p) }));

    // Group all packaging variants (e.g. Digikey's CT/TR/Digi-Reel) under a
    // single per-distributor option so the UI shows one row per distributor
    // (matching how Digikey's own site presents packaging as a sub-detail,
    // not a separate line item).
    const distributorGroups = exactMatches.reduce((acc, p) => {
      const dist = p.distributor;
      if (!acc[dist]) acc[dist] = [];
      acc[dist].push(p);
      return acc;
    }, {});

    // Raw packaging variants per distributor for the price-slab modal, sourced
    // from `productsWithSlab` (pre split-dedup) so a packaging type that lost
    // out to a cheaper split-order combo (e.g. Digikey's Tape & Reel) still
    // keeps its own full quantity break table instead of disappearing.
    const exactProductsWithSlab = productsWithSlab.filter(
      (p) => normalizePartNumber(p.part_number) === itemPartNumber,
    );
    const packageGroups = exactProductsWithSlab.reduce((acc, p) => {
      const dist = p.distributor;
      if (!acc[dist]) acc[dist] = [];
      acc[dist].push(p);
      return acc;
    }, {});

    const distributorOptions = Object.entries(distributorGroups)
      .map(([dist, products]) => {
        // Representative product for this distributor's row: cheapest match,
        // which may be a split-order combo if that beats any single packaging.
        // Skip below-min-order packagings (can't be bought from current stock)
        // so a reel-only bulk price doesn't hijack the row; fall back to them
        // only if that distributor has nothing else.
        const orderable = products.filter((p) => !p.belowStockMin);
        const pickFrom = orderable.length > 0 ? orderable : products;
        const representative = pickFrom.reduce((min, p) => {
          const pTotal = calcTotal(p);
          return !min || pTotal < min.totalPrice
            ? { ...p, totalPrice: pTotal }
            : min;
        }, null);

        const packageMap = {};
        for (const p of packageGroups[dist] ?? []) {
          const packageType = p.price?.package_type ?? "";
          const pTotal = calcTotal(p);
          const existing = packageMap[packageType];
          if (!existing || pTotal < existing.totalPrice) {
            packageMap[packageType] = { ...p, totalPrice: pTotal };
          }
        }
        const packageOptions = dedupePackageOptionsByPriceList(
          sortPackageOptions(Object.values(packageMap)),
        );

        return {
          key: dist,
          distributor: dist,
          label: dist,
          product: representative,
          packageOptions,
        };
      })
      .sort((a, b) => (a.product.totalPrice ?? 0) - (b.product.totalPrice ?? 0));

    const perDistributorBest = Object.values(
      exactProductsWithSlab
        .filter((p) => !p.belowStockMin)
        .reduce((acc, p) => {
        const dist = p.distributor;
        const unit = getSlabUnitPrice(p.selectedPrice);
        const avail = getNumericValue(p.availability);
        const existing = acc[dist];
        if (!existing) {
          acc[dist] = { ...p, _unit: unit, _maxAvail: avail };
        } else if (unit < existing._unit) {
          acc[dist] = {
            ...p,
            _unit: unit,
            _maxAvail: Math.max(existing._maxAvail, avail),
          };
        } else {
          existing._maxAvail = Math.max(existing._maxAvail, avail);
        }
        return acc;
      }, {}),
    ).map((p) => ({ ...p, displayAvailability: p._maxAvail }));
    const crossDistributorSplit = isAnyStockFallback
      ? getCrossDistributorSplit(perDistributorBest, requiredQty)
      : null;

    // When the exact-match part exists in the response but has no real stock
    // from any selected distributor (itemStatus = STOCK_NOT_FOUND), the pool
    // above can still produce a "winner" sourced from a non-exact similar
    // part. Returning that as the optimal misleads the user, so drop it.
    if (itemStatus === BOM_ITEM_STATUS.STOCK_NOT_FOUND) {
      return {
        id: item.id,
        part_number: item.part_number,
        qty: item.qty,
        actual_qty: item.actual_qty ?? null,
        optimal: null,
        otherMatches,
        distributorOptions: [],
        optimalKey: null,
        savedDistributorKey: item.selected_distributor ?? null,
        distributorErrors,
        distributorEmptyResults,
        itemStatus,
      };
    }

    const optimalKey = optimal?.distributor ?? null;

    const optimalResult = {
      ...optimal,
      totalPrice: calcTotal(optimal),
      hasFullStock: sufficientStock.length > 0,
      isExactMatch: exactMatches.length > 0,
    };
    const optimalSavings = computeOrderSavings(
      optimalResult,
      distributorOptions,
      optimalKey,
    );
    return {
      id: item.id,
      part_number: item.part_number,
      qty: item.qty,
      actual_qty: item.actual_qty ?? null,
      optimal: optimalResult,
      optimalSavings,
      otherMatches,
      distributorOptions,
      crossDistributorSplit,
      optimalKey,
      savedDistributorKey: item.selected_distributor ?? null,
      distributorErrors,
      distributorEmptyResults,
      itemStatus,
    };
  });
};

// The price_list slab to show for a product under the "Price: RFQ Qty" toggle.
// Stock-Qty (default) keeps the optimizer's stock-capped selectedPrice; RFQ-Qty
// returns the slab the full requested qty qualifies for. Splits keep their
// blended selectedPrice (no single slab represents a weighted average).
export const getEffectivePriceSlab = (product, requiredQty, priceByRfqQty) => {
  if (!product) return null;
  if (priceByRfqQty && !product.isSplitOrder) {
    const actualQty = getActualOrderQty(product, requiredQty);
    return (
      getMatchingPriceSlab(product, actualQty) ?? product.selectedPrice ?? null
    );
  }
  return product.selectedPrice ?? null;
};

// Resolve a product's effective unit price / total / MOQ for the download,
// honouring the "Price: RFQ Qty" toggle. Stock-Qty basis (default) mirrors the
// pre-existing selectedPrice logic exactly, including the optimizer's
// stock-capped totalPrice. RFQ-Qty basis reprices at the slab the full
// requested qty qualifies for and recomputes the total as unit × ordered qty so
// the sheet stays internally consistent. Split orders keep their weighted
// selectedPrice in both modes (a blended price has no single price_list slab).
const priceProductForBasis = (product, requiredQty, priceByRfqQty) => {
  if (!product)
    return {
      unitPrice: null,
      totalPrice: null,
      actualOrderQty: null,
      minQty: null,
    };
  if (priceByRfqQty && !product.isSplitOrder) {
    const actualQty = getActualOrderQty(product, requiredQty);
    const slab =
      getMatchingPriceSlab(product, actualQty) ?? product.selectedPrice;
    const unitPrice = getSlabUnitPrice(slab);
    return {
      unitPrice,
      totalPrice: unitPrice * actualQty,
      actualOrderQty: actualQty,
      minQty: slab?.min_qty ?? actualQty,
    };
  }
  return {
    unitPrice: product.isSplitOrder
      ? (product.selectedPrice?.price ?? 0)
      : getSlabUnitPrice(product.selectedPrice),
    totalPrice: product.totalPrice ?? 0,
    actualOrderQty: product.actualOrderQty,
    minQty: product.selectedPrice?.min_qty,
  };
};

export const buildBomExcelData = (
  rawBomItems,
  bomItems,
  priceByRfqQty = false,
) => {
  if (!Array.isArray(rawBomItems))
    return {
      distributors: [],
      rows: [],
      distributorSummary: [],
      optimalGrandTotal: 0,
    };

  const distributorSet = new Set();
  for (const item of rawBomItems) {
    for (const pd of item.processing_data || []) {
      if (pd.distributor) distributorSet.add(pd.distributor);
    }
  }
  const distributors = [...distributorSet];

  const bomItemMap = Object.fromEntries((bomItems || []).map((b) => [b.id, b]));

  const distSummaryMap = {};
  for (const dist of distributors) {
    distSummaryMap[dist] = {
      distributor: dist,
      totalPrice: 0,
      availableCount: 0,
      unavailableCount: 0,
      currency: "",
    };
  }
  let optimalGrandTotal = 0;

  const rows = [];
  for (const item of rawBomItems) {
    const requiredQty =
      parseInt(item.actual_qty, 10) || parseInt(item.qty, 10) || 0;
    const processedItem = bomItemMap[item.id];
    const itemPartLower = normalizePartNumber(item.part_number);

    const distPrices = {};
    const distStock = {};
    for (const pd of item.processing_data || []) {
      const dist = pd.distributor;
      const allProducts = Array.isArray(pd.data) ? pd.data : [];
      const exactProducts = allProducts.filter(
        (p) => normalizePartNumber(p.part_number) === itemPartLower,
      );
      const products = exactProducts.length > 0 ? exactProducts : allProducts;

      // Availability/stock should be captured even if we can't compute pricing slabs
      const maxAvail = products.reduce(
        (max, p) => Math.max(max, getNumericValue(p?.availability)),
        0,
      );
      distStock[dist] = maxAvail;

      const withSlab = products
        .map((p) => {
          const actualQty = getActualOrderQty(p, requiredQty);
          const slab = getMatchingPriceSlab(p, actualQty);
          if (!slab) return null;
          const unitPrice = getSlabUnitPrice(slab);
          return {
            unitPrice,
            totalPrice: unitPrice * actualQty,
            actualOrderQty: actualQty,
            avail: getNumericValue(p.availability),
            currency: slab.currency ?? "",
            slab,
          };
        })
        .filter(Boolean);
      const inStock = withSlab.filter((p) => p.avail > 0);
      const pool = inStock.length > 0 ? inStock : withSlab;
      const best =
        pool.length > 0
          ? pool.reduce((min, p) => (p.totalPrice < min.totalPrice ? p : min))
          : null;
      distPrices[dist] = best;

      if (best == null) {
        distSummaryMap[dist].unavailableCount += 1;
      } else {
        distSummaryMap[dist].totalPrice += best.totalPrice;
        distSummaryMap[dist].availableCount += 1;
        if (!distSummaryMap[dist].currency && best.currency)
          distSummaryMap[dist].currency = best.currency;
      }
    }

    // Replace each distributor's entry with the cheapest deduped distributorOption
    // (the same pool the BOM UI shows per-distributor, including splits with the
    // non-split source product dropped). This keeps the raw download in sync with
    // the table — e.g. a Digikey split won't be shadowed by the rounded-up
    // non-split TR variant just because that variant happens to be cheaper.
    const bestOptionByDist = {};
    for (const o of processedItem?.distributorOptions ?? []) {
      const dist = o.distributor;
      const cur = bestOptionByDist[dist];
      const oTotal = o.product?.totalPrice ?? 0;
      if (!cur || oTotal < (cur.product?.totalPrice ?? 0)) {
        bestOptionByDist[dist] = o;
      }
    }
    for (const dist of Object.keys(bestOptionByDist)) {
      const sp = bestOptionByDist[dist].product;
      const spPriced = priceProductForBasis(sp, requiredQty, priceByRfqQty);
      const newTotal = spPriced.totalPrice ?? 0;
      const current = distPrices[dist];
      const summary = distSummaryMap[dist];
      if (summary) {
        if (current == null) {
          summary.unavailableCount = Math.max(
            0,
            summary.unavailableCount - 1,
          );
          summary.availableCount += 1;
        } else {
          summary.totalPrice -= current.totalPrice;
        }
        summary.totalPrice += newTotal;
        if (!summary.currency && (sp.price?.currency ?? "")) {
          summary.currency = sp.price?.currency ?? "";
        }
      }
      distPrices[dist] = {
        unitPrice: spPriced.unitPrice,
        totalPrice: newTotal,
        actualOrderQty: spPriced.actualOrderQty,
        minQty: spPriced.minQty,
        avail: getNumericValue(sp.displayAvailability ?? sp.availability),
        currency: sp.price?.currency ?? "",
      };
    }

    const optimal = processedItem?.optimal ?? null;
    const optimalPriced = priceProductForBasis(
      optimal,
      requiredQty,
      priceByRfqQty,
    );
    if (optimalPriced.totalPrice) optimalGrandTotal += optimalPriced.totalPrice;

    // Ensure the chosen optimal distributor column reflects the same unit/total used in the optimal selection.
    // (The raw distPrices computation above is a best-effort approximation and can differ due to stock rules.)
    if (optimal?.distributor) {
      const optDist = optimal.distributor;
      const prev = distPrices[optDist] ?? null;

      const optUnitPrice = optimalPriced.unitPrice;
      const optTotalPrice = optimalPriced.totalPrice;
      const optActualQty = optimalPriced.actualOrderQty ?? null;

      if (optUnitPrice != null && optTotalPrice != null) {
        distPrices[optDist] = {
          unitPrice: optUnitPrice,
          totalPrice: optTotalPrice,
          actualOrderQty: optActualQty,
          minQty: optimalPriced.minQty ?? optActualQty,
          avail: getNumericValue(
            optimal.displayAvailability ?? optimal.availability,
          ),
          currency: optimal.price?.currency ?? "",
        };

        const summary = distSummaryMap[optDist];
        if (summary) {
          if (prev == null) {
            summary.unavailableCount = Math.max(
              0,
              summary.unavailableCount - 1,
            );
            summary.availableCount += 1;
          } else {
            summary.totalPrice -= prev.totalPrice;
          }
          summary.totalPrice += optTotalPrice;
          if (!summary.currency && (optimal.price?.currency ?? "")) {
            summary.currency = optimal.price?.currency ?? "";
          }
        }
      }
    }

    const optSplitDetails = optimal?.isSplitOrder
      ? `${optimal.splitFirst?.qty?.toLocaleString()} ${optimal.price?.package_type?.split("+")[0] ?? "TR"} @ ${optimal.price?.currency} ${optimal.splitFirst?.unitPrice} + ${optimal.splitSecond?.qty?.toLocaleString()} ${optimal.splitSecond?.product?.price?.package_type ?? "CT"} @ ${optimal.splitSecond?.product?.price?.currency ?? optimal.price?.currency} ${optimal.splitSecond?.unitPrice}`
      : null;

    rows.push({
      part_number: item.part_number,
      qty: requiredQty,
      optStock: optimal
        ? getNumericValue(optimal.displayAvailability ?? optimal.availability)
        : null,
      distPrices,
      distStock,
      optimalDistributor: optimal?.distributor ?? null,
      optUnitPrice: optimalPriced.unitPrice,
      optActualOrderQty: optimalPriced.minQty ?? null,
      optTotalPrice: optimalPriced.totalPrice,
      optSplitDetails,
      rawItemStatus: processedItem?.itemStatus ?? null,
      optStatus: (() => {
        const labelForStatus = (s) => {
          switch (s) {
            case BOM_ITEM_STATUS.NO_MPN_FOUND:
              return "No MPN found";
            case BOM_ITEM_STATUS.STOCK_NOT_FOUND:
              return "No Stock";
            case BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND:
              return "Multiple MPN found";
            case BOM_ITEM_STATUS.FOUND:
              return "Found";
            default:
              return s ?? "-";
          }
        };
        if (!optimal) return labelForStatus(processedItem?.itemStatus);
        const avail = getNumericValue(
          optimal.displayAvailability ?? optimal.availability,
        );
        if (avail > 0 && avail < requiredQty)
          return `Partial Stock (${avail} in stock)`;
        return labelForStatus(processedItem?.itemStatus ?? BOM_ITEM_STATUS.FOUND);
      })(),
      optSavings: (() => {
        const savings = processedItem?.optimalSavings;
        if (savings == null) return null;
        const currency = optimal?.price?.currency ?? "";
        const actualQty = optimal?.actualOrderQty ?? null;
        const rounding =
          actualQty > requiredQty ? `↑ from ${requiredQty} · ` : "";
        return `${rounding}saves ${currency} ${savings}`.trim();
      })(),
    });
  }

  const distributorSummary = Object.values(distSummaryMap).sort(
    (a, b) => a.totalPrice - b.totalPrice,
  );

  return { distributors, rows, distributorSummary, optimalGrandTotal };
};

export const buildBomExcelBuffer = async (excelData) => {
  const {
    distributors,
    rows,
    distributorSummary: distSummary,
    optimalGrandTotal,
  } = excelData;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("BOM Price Data");

  const columns = [
    { header: "Part Number", key: "part_number", width: 28 },
    { header: "Req Qty", key: "qty", width: 10 },
  ];
  for (const dist of distributors) {
    columns.push({ header: `${dist} Stock`, key: `${dist}_stock`, width: 14 });
    columns.push({
      header: `${dist} Unit Price`,
      key: `${dist}_unit`,
      width: 18,
    });
    columns.push({ header: `${dist} MOQ`, key: `${dist}_moq`, width: 10 });
    columns.push({ header: `${dist} Total`, key: `${dist}_total`, width: 16 });
  }
  columns.push({ header: "Optimal Distributor", key: "opt_dist", width: 20 });
  columns.push({ header: "Optimal Unit Price", key: "opt_unit", width: 18 });
  columns.push({ header: "Optimal MOQ", key: "opt_moq", width: 12 });
  columns.push({ header: "Optimal Total", key: "opt_total", width: 16 });
  columns.push({ header: "Split Details", key: "opt_split", width: 44 });
  columns.push({ header: "Status", key: "opt_status", width: 28 });
  sheet.columns = columns;

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: "FF1E293B" } };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD9E1F2" },
  };
  headerRow.alignment = { horizontal: "center" };

  const distributorFillPalette = [
    "FFE0F2FE", // sky-100
    "FFDCFCE7", // green-100
    "FFFEF9C3", // yellow-100
    "FFFFEDD5", // orange-100
    "FFFCE7F3", // pink-100
    "FFEDE9FE", // violet-100
    "FFECFCCB", // lime-100
    "FFF1F5F9", // slate-100
  ];

  const getDistributorFillColor = (distributorName, index) => {
    const name = String(distributorName ?? "").toLowerCase();

    // Explicit mappings requested
    if (name.includes("digikey")) return "FFFEE2E2"; // red-100
    if (name.includes("element14") || name.includes("element 14"))
      return "FFDCFCE7"; // green-100
    if (name.includes("mouser") || name.includes("mosuer")) return "FFE0F2FE"; // sky-100
    if (name.includes("tme")) return "FFFEF9C3"; // yellow-100
    if (name.includes("rutronik")) return "FFDBEAFE"; // blue-100

    return distributorFillPalette[index % distributorFillPalette.length];
  };

  for (let i = 0; i < distributors.length; i += 1) {
    const fillColor = getDistributorFillColor(distributors[i], i);
    const startCol = 3 + i * 4;
    for (let c = startCol; c < startCol + 4; c += 1) {
      const cell = headerRow.getCell(c);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: fillColor },
      };
    }
  }

  for (const row of rows) {
    const rowData = {
      part_number: row.part_number,
      qty: row.qty,
    };
    const isMultiMpn = row.rawItemStatus === BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND;
    for (const dist of distributors) {
      const dp = row.distPrices[dist];
      const ds = row.distStock?.[dist];
      rowData[`${dist}_stock`] = isMultiMpn ? "-" : ds != null ? ds : "-";
      rowData[`${dist}_unit`] = isMultiMpn
        ? "-"
        : dp != null
          ? parseFloat(dp.unitPrice.toFixed(6))
          : "-";
      rowData[`${dist}_moq`] = isMultiMpn
        ? "-"
        : dp != null
          ? (dp.minQty ?? dp.slab?.min_qty ?? dp.actualOrderQty)
          : "-";
      rowData[`${dist}_total`] = isMultiMpn
        ? "-"
        : dp != null
          ? parseFloat(dp.totalPrice.toFixed(4))
          : "-";
    }
    rowData["opt_dist"] = isMultiMpn ? "-" : (row.optimalDistributor ?? "-");
    rowData["opt_unit"] = isMultiMpn
      ? "-"
      : row.optUnitPrice != null
        ? parseFloat(row.optUnitPrice.toFixed(6))
        : "-";
    rowData["opt_moq"] = isMultiMpn ? "-" : (row.optActualOrderQty ?? "-");
    rowData["opt_total"] = isMultiMpn
      ? "-"
      : row.optTotalPrice != null
        ? parseFloat(row.optTotalPrice.toFixed(4))
        : "-";
    rowData["opt_savings"] = isMultiMpn ? "-" : (row.optSavings ?? "-");
    rowData["opt_split"] = isMultiMpn ? "-" : (row.optSplitDetails ?? "-");
    rowData["opt_status"] = row.optStatus ?? "-";
    sheet.addRow(rowData);
  }

  const totalsData = { part_number: "GRAND TOTAL", qty: "" };
  for (const dist of distributors) {
    const ds = distSummary.find((d) => d.distributor === dist);
    totalsData[`${dist}_unit`] = "";
    totalsData[`${dist}_moq`] = "";
    totalsData[`${dist}_total`] = parseFloat((ds?.totalPrice ?? 0).toFixed(4));
  }
  totalsData["opt_dist"] = "";
  totalsData["opt_unit"] = "";
  totalsData["opt_moq"] = "";
  totalsData["opt_total"] = parseFloat(optimalGrandTotal.toFixed(4));
  totalsData["opt_split"] = "";
  totalsData["opt_status"] = "";
  const totalsRow = sheet.addRow(totalsData);
  totalsRow.font = { bold: true, color: { argb: "FF1E293B" } };
  totalsRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFD966" },
  };
  totalsRow.alignment = { horizontal: "center" };

  const lastRowIdx = sheet.rowCount;
  for (let i = 0; i < distributors.length; i += 1) {
    const fillColor = getDistributorFillColor(distributors[i], i);
    const startCol = 3 + i * 4;
    for (let r = 2; r <= lastRowIdx - 1; r += 1) {
      const row = sheet.getRow(r);
      for (let c = startCol; c < startCol + 4; c += 1) {
        const cell = row.getCell(c);
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fillColor },
        };
      }
    }
  }

  return workbook.xlsx.writeBuffer();
};

export const getBomDistributorSummary = (bomItems) => {
  if (!Array.isArray(bomItems)) return [];

  const map = {};
  for (const item of bomItems) {
    const { optimal } = item;
    if (!optimal) continue;
    const dist = optimal.distributor || "Unknown";
    if (!map[dist]) {
      map[dist] = {
        distributor: dist,
        itemCount: 0,
        totalPrice: 0,
        currency: optimal.price?.currency ?? "",
      };
    }
    map[dist].itemCount += 1;
    map[dist].totalPrice += optimal.totalPrice || 0;
  }

  return Object.values(map).sort((a, b) => b.totalPrice - a.totalPrice);
};
